/**
 * Kids permissions — recurring auto-allow windows (school bus, classes,
 * coaching) with an enable toggle per rule, add new window. Wired: GET/POST
 * /api/services/kids-permission. Port of KidsPermission in
 * design/source/screens-services.jsx.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, str, useGateList } from '@/components/gate/api';
import { InfoNote, IconTile, Toggle } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, Chip, EmptyState, Field, IconButton, Input, Pill, Screen, Sheet, Skeleton, TopBar } from '@/ui';

type Window_ = {
  id: string; kidName: string; label: string; when: string; who: string; enabled: boolean;
  oneShot: boolean; expired: boolean; expiresAt?: string;
};

function toWindow(w: unknown, i: number): Window_ {
  const kidName = str(w, 'kidName') ?? '';
  return {
    id: str(w, 'id') ?? `w${i}`,
    kidName,
    label: str(w, 'label', 'title', 'allowedActivity') ?? 'Recurring window',
    when: str(w, 'when', 'schedule', 'windowRule') ?? '',
    who: str(w, 'who', 'escort') ?? '',
    enabled: bool(w, 'enabled', 'on', 'active') ?? true,
    oneShot: bool(w, 'oneShot') ?? false,
    expired: bool(w, 'expired') ?? false,
    expiresAt: str(w, 'expiresAt'),
  };
}

function untilLabel(iso?: string): string {
  if (!iso) return 'ACTIVE';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'ACTIVE';
  let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
  return `UNTIL ${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
}

export default function KidsPermissionScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, setItems, state, reload } = useGateList<unknown>('/api/services/kids-permission', 'windows', 'rules');
  const rows = items.map(toWindow);
  const [notice, setNotice] = React.useState<string | null>(null);

  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [kidName, setKidName] = React.useState('');
  const [windowRule, setWindowRule] = React.useState('');
  const [allowedActivity, setAllowedActivity] = React.useState('');
  const [escort, setEscort] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [removing, setRemoving] = React.useState(false);

  const openAdd = () => {
    setEditingId(null);
    setKidName(''); setWindowRule(''); setAllowedActivity(''); setEscort('');
    setSheetOpen(true);
  };

  const openEdit = (w: Window_) => {
    setEditingId(w.id);
    setKidName(w.kidName);
    setWindowRule(w.when);
    setAllowedActivity(w.label === 'Recurring window' ? '' : w.label);
    setEscort(w.who);
    setSheetOpen(true);
  };

  const toggleWindow = async (w: Window_) => {
    const prevItems = items;
    setItems(items.map((raw) => (str(raw, 'id') === w.id ? { ...(raw as object), enabled: !w.enabled } : raw)));
    setNotice(null);
    try {
      await api.post<unknown>(`/api/services/kids-permission/${encodeURIComponent(w.id)}`, { enabled: !w.enabled });
    } catch {
      setItems(prevItems);
      setNotice(t('kp_toggle_error'));
    }
  };

  // quick one-shot ("allow my kid to exit in next X hours")
  const knownKids = [...new Set(rows.map((r) => r.kidName).filter(Boolean))];
  const [quickKid, setQuickKid] = React.useState('');
  const [quickHours, setQuickHours] = React.useState(1);
  const [quickSaving, setQuickSaving] = React.useState(false);
  const quickName = quickKid || knownKids[0] || '';

  const allowOut = async () => {
    if (!quickName.trim() || quickSaving) return;
    setQuickSaving(true);
    setNotice(null);
    try {
      await api.post<unknown>('/api/services/kids-permission', {
        kidName: quickName.trim(),
        oneShot: true,
        hours: quickHours,
      });
      await reload();
    } catch {
      setNotice(t('kp_allow_error'));
    } finally {
      setQuickSaving(false);
    }
  };

  const canAddWindow = kidName.trim().length > 0 && windowRule.trim().length > 0;

  const saveWindow = async () => {
    if (!canAddWindow || saving) return;
    setSaving(true);
    setNotice(null);
    const payload = {
      kidName: kidName.trim(),
      windowRule: windowRule.trim(),
      allowedActivity: allowedActivity.trim(),
      escort: escort.trim(),
    };
    try {
      if (editingId) {
        await api.post<unknown>(`/api/services/kids-permission/${encodeURIComponent(editingId)}`, payload);
      } else {
        await api.post<unknown>('/api/services/kids-permission', { ...payload, enabled: true });
      }
      setSheetOpen(false);
      await reload();
    } catch {
      setNotice(editingId ? t('kp_save_error') : t('kp_add_error'));
    } finally {
      setSaving(false);
    }
  };

  const removeWindow = async () => {
    if (!editingId || removing) return;
    setRemoving(true);
    setNotice(null);
    try {
      await api.post<unknown>(`/api/services/kids-permission/${encodeURIComponent(editingId)}`, { isActive: false });
      setSheetOpen(false);
      await reload();
    } catch {
      setNotice(t('kp_remove_error'));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('kp_title')}
        onBack={() => router.back()}
        right={
          <IconButton label={t('kp_add_window')} onPress={openAdd}>
            <Icon name="plus" size={22} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <Card style={{ marginBottom: space.s4 }}>
          <AppText variant="bodyMd" weight="500">{t('kp_allow_now')}</AppText>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
            {t('kp_allow_now_sub')}
          </AppText>
          {knownKids.length > 1 ? (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2, marginTop: space.s3 }}>
              {knownKids.map((k) => (
                <Chip key={k} label={k.split(' ')[0]} active={quickName === k} onPress={() => setQuickKid(k)} />
              ))}
            </View>
          ) : null}
          {knownKids.length === 0 ? (
            <Input
              value={quickKid}
              onChangeText={setQuickKid}
              placeholder={t('kp_child_name')}
              style={{ marginTop: space.s3 }}
            />
          ) : null}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2, marginTop: space.s3 }}>
            {[1, 2, 4, 8].map((h) => (
              <Chip key={h} label={h === 1 ? t('pa_one_hour') : t('pa_n_hours', { n: h })} active={quickHours === h} onPress={() => setQuickHours(h)} />
            ))}
          </View>
          <Button
            title={quickName ? t('kp_allow_named', { name: quickName.split(' ')[0], h: quickHours }) : t('kp_allow_out')}
            loading={quickSaving}
            disabled={!quickName.trim()}
            onPress={() => void allowOut()}
            style={{ marginTop: space.s3 }}
          />
        </Card>

        <InfoNote icon="clock" style={{ marginBottom: space.s4 }}>
          {t('kp_info')}
        </InfoNote>

        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2].map((i) => <Skeleton key={i} h={80} />)}
          </View>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="clock" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('kp_err_title') : t('kp_empty_title')}
            body={state === 'error' ? t('kp_err_body') : t('kp_empty_body')}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {rows.map((w) => (
              <Card key={w.id} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                <Pressable
                  onPress={() => openEdit(w)}
                  accessibilityRole="button"
                  accessibilityLabel={`Edit ${w.kidName} ${w.label}`}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, flex: 1, minWidth: 0 }}
                >
                  <IconTile name="clock" />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <AppText variant="bodyMd" weight="500">{w.kidName || t('kp_child')} · {w.label}</AppText>
                    {w.when ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{w.when}</AppText> : null}
                    {w.who ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{w.who}</AppText> : null}
                  </View>
                </Pressable>
                {w.oneShot ? (
                  <Pill status={w.expired ? 'pending' : 'resolved'} label={w.expired ? 'EXPIRED' : untilLabel(w.expiresAt)} />
                ) : (
                  <Toggle on={w.enabled} onChange={() => void toggleWindow(w)} label={`Toggle ${w.kidName} ${w.label}`} />
                )}
              </Card>
            ))}
          </View>
        )}
        {notice ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s4 }}>{notice}</AppText> : null}
      </Screen>

      <Sheet visible={sheetOpen} onClose={() => setSheetOpen(false)}>
        <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s4 }}>
          {editingId ? t('kp_edit_window') : t('kp_add_recurring')}
        </AppText>
        <View style={{ gap: space.s4 }}>
          <Field label={t('kp_child_name')}>
            <Input value={kidName} onChangeText={setKidName} placeholder={t('kp_kid_ph')} />
          </Field>
          <Field label={t('kp_activity')}>
            <Input value={allowedActivity} onChangeText={setAllowedActivity} placeholder={t('kp_activity_ph')} />
          </Field>
          <Field label={t('kp_schedule')}>
            <Input value={windowRule} onChangeText={setWindowRule} placeholder={t('kp_schedule_ph')} />
          </Field>
          <Field label={t('kp_escort')}>
            <Input value={escort} onChangeText={setEscort} placeholder={t('kp_escort_ph')} />
          </Field>
        </View>
        <Button
          title={editingId ? t('kp_save') : t('kp_add_window')}
          size="lg"
          loading={saving}
          disabled={!canAddWindow}
          onPress={() => void saveWindow()}
          style={{ marginTop: space.s5 }}
        />
        {editingId ? (
          <Button
            title={t('kp_remove')}
            kind="destructive"
            loading={removing}
            onPress={() => void removeWindow()}
            style={{ marginTop: space.s3 }}
          />
        ) : null}
      </Sheet>
    </Screen>
  );
}
